import {
  CmpEventPaymentStatusEnum,
  CmpEventSponsorshipRequestStatusEnum,
} from "../../../../graphql/types";
import { parseEventInvoiceDraftForm } from "./eventInvoiceDrafts";
import { isComplimentaryEventPaymentStatus } from "./eventInvoiceStatus";

// Manage Tickets Live Analytics, CMP Dashboard (21) ticketStats
// (:19106-19133): seven cards and
// available = capacity − (sold + awaiting + sponsor + complimentary).
// Counts are attendee places (a Table of 8 holds 8), as the capacity is.

export type EventTicketStatsRegistration = {
  paymentStatus: CmpEventPaymentStatusEnum;
  reservedAttendeeCount: number;
};

export type EventTicketStatsSponsorship = {
  attendeeCount: number;
  status: CmpEventSponsorshipRequestStatusEnum;
};

export type EventTicketStatsCard = {
  label: string;
  value: string;
  footnote?: string;
};

type DraftTicketLine = { quantity?: unknown; ticketTypeId?: unknown };

/**
 * Places on saved Create Ticket Invoice drafts (IF11 Q8, option B). A draft
 * holds no places, so only these cards count it, as Allocated – Awaiting
 * Payment. A draft that cannot be read counts 0.
 */
export const getEventTicketDraftAttendeeCount = ({
  draftForms,
  ticketTypes,
}: {
  draftForms: ReadonlyArray<string | null | undefined>;
  ticketTypes: ReadonlyArray<{ id: string; attendeesPerTicket?: number | null }>;
}): number => {
  const attendeesPerTicketById = new Map(
    ticketTypes.map((ticketType) => [
      ticketType.id,
      Math.max(1, Math.floor(ticketType.attendeesPerTicket || 1)),
    ]),
  );
  return draftForms.reduce((total, draftForm) => {
    const form = parseEventInvoiceDraftForm<{ ticketLines?: DraftTicketLine[] }>(
      draftForm,
    );
    const lines = Array.isArray(form?.ticketLines) ? form!.ticketLines : [];
    return (
      total +
      lines.reduce((lineTotal, line) => {
        const quantity = Math.floor(Number(line.quantity) || 0);
        const ticketTypeId = typeof line.ticketTypeId === "string" ? line.ticketTypeId : "";
        if (quantity <= 0 || !ticketTypeId) return lineTotal;
        return lineTotal + quantity * (attendeesPerTicketById.get(ticketTypeId) || 1);
      }, 0)
    );
  }, 0);
};

export const getEventTicketStatsCards = ({
  draftAttendeeCount,
  maxAttendeeCapacity,
  registrations,
  revenueLabel,
  sponsorships,
}: {
  draftAttendeeCount: number;
  maxAttendeeCapacity: number | null | undefined;
  registrations: ReadonlyArray<EventTicketStatsRegistration>;
  revenueLabel: string;
  sponsorships: ReadonlyArray<EventTicketStatsSponsorship>;
}): EventTicketStatsCard[] => {
  let sold = 0;
  let awaiting = draftAttendeeCount;
  let complimentary = 0;
  registrations.forEach(({ paymentStatus, reservedAttendeeCount }) => {
    if (reservedAttendeeCount <= 0) return;
    if (isComplimentaryEventPaymentStatus(paymentStatus)) {
      complimentary += reservedAttendeeCount;
    } else if (paymentStatus === CmpEventPaymentStatusEnum.Paid) {
      sold += reservedAttendeeCount;
    } else {
      awaiting += reservedAttendeeCount;
    }
  });
  // IF11 Q7: sponsorship places count as issued before the sponsorship is paid.
  const sponsor = sponsorships.reduce(
    (total, { attendeeCount, status }) =>
      status === CmpEventSponsorshipRequestStatusEnum.Canceled ||
      status === CmpEventSponsorshipRequestStatusEnum.Rejected
        ? total
        : total + attendeeCount,
    0,
  );
  const capacity =
    maxAttendeeCapacity && maxAttendeeCapacity > 0 ? maxAttendeeCapacity : 0;
  const available = Math.max(0, capacity - (sold + awaiting + sponsor + complimentary));

  return [
    {
      label: "Event Capacity",
      value: capacity ? String(capacity) : "—",
      footnote: capacity ? undefined : "*Set an Event Capacity in Create Event",
    },
    { label: "Tickets Sold & Issued", value: String(sold), footnote: "*Paid tickets" },
    { label: "Tickets Allocated – Awaiting Payment", value: String(awaiting) },
    {
      label: "Complimentary Sponsor Tickets Issued",
      value: String(sponsor),
      footnote: "*Included with sponsorships",
    },
    {
      label: "Complimentary Tickets Issued",
      value: String(complimentary),
      footnote: "*$0 invoiced tickets",
    },
    {
      label: "Tickets Available",
      value: capacity ? String(available) : "—",
      footnote: capacity
        ? `*${capacity} − ${sold} − ${awaiting} − ${sponsor} − ${complimentary}`
        : undefined,
    },
    {
      label: "Total Ticket Revenue",
      value: revenueLabel,
      footnote: "• Includes paid ticket sales only",
    },
  ];
};
